import { CustomerDTO } from "@/modules/clients/dtos";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { getCasesByCrmJourneyUid, getWorkflowBoard } from "../api";
import { JourneyCase, ParticipantBoardResponse } from "../dtos";
import {
  CASE_UID_PARAM,
  resolveSelectedCase,
  sortJourneyCases,
} from "../utils/case-selection";
import { deriveBoard } from "../utils/derive-board";
import { realtimeQueryFlags, useNawyNowRealtimeLive } from "./realtime-status";

/**
 * Lead → cases resolution by crmJourneyUid — the canonical CRM ↔ case link, straight against
 * workflow-manager (the source of truth, so a case started a moment ago resolves immediately —
 * no replication lag). Both creation origins persist it (CRM-initiated forwards the lead's
 * journey uid; WM-initiated stores the uid from link-or-create-nawy-now), so a case shows up
 * regardless of which side started it.
 *
 * §Multi-application: a journey may carry SEVERAL applications (ACTIVE ones included), so this returns
 * them all; `useCaseSelection` picks the one on screen (the URL's `caseUid`, else the newest ACTIVE).
 *
 * TODO(nawy-now): co-applicant/guarantor leads (no journey of their own) need a
 * participant-phone fallback — blocked on workflow-manager shipping
 * GET /v1/cases/by-participant (see getCasesByParticipant in ../api).
 */
const fetchJourneyCases = async (
  journeyUid: string | undefined
): Promise<JourneyCase[]> => {
  if (!journeyUid) return [];
  const { results } = await getCasesByCrmJourneyUid(journeyUid);
  return results;
};

/** §NetAudit (2026-08-05): several lead-page hosts need the lead ↔ case answer (this board, the
 * orange start-flow banner, the status banner) — ONE query key so they all ride the same request.
 * staleTime keeps a later mount (tab content after the banner) from refetching what the first
 * mount just loaded; mutations bypass it via invalidateQueries, the refresh button via refetch. */
const RESOLUTION_STALE_MS = 30_000;

/* §Realtime-poll-gate: the shared freshness policy lives with the liveness signal it reads —
 * see `realtimeQueryFlags` in ./realtime-status. */

export const useCaseResolution = (journeyUid?: string) => {
  const live = useNawyNowRealtimeLive();
  return useQuery({
    queryKey: ["nawy-now", "case-resolution", journeyUid ?? null],
    keepPreviousData: true,
    staleTime: RESOLUTION_STALE_MS,
    ...realtimeQueryFlags(live),
    queryFn: () => fetchJourneyCases(journeyUid),
  });
};

/**
 * §Multi-application: which of the journey's applications the lead page shows. The URL's `caseUid` is the
 * single source of truth — the tab and the above-the-tabs status banner both mount this hook, and a
 * notification deep link carries it — so nothing here ever writes the URL on its own (two mounts would race);
 * only an explicit `selectCase` does. An absent or foreign `caseUid` falls back to the newest ACTIVE case.
 */
export const useCaseSelection = (journeyUid?: string) => {
  const resolutionQuery = useCaseResolution(journeyUid);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedUid = searchParams.get(CASE_UID_PARAM);
  const cases = useMemo(
    () => sortJourneyCases(resolutionQuery.data ?? []),
    [resolutionQuery.data]
  );
  const selectedCase = useMemo(
    () => resolveSelectedCase(cases, requestedUid),
    [cases, requestedUid]
  );
  const selectCase = useCallback(
    (caseUid: string) =>
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set(CASE_UID_PARAM, caseUid);
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );
  return {
    resolutionQuery,
    cases,
    selectedCase,
    selectedCaseUid: selectedCase?.uid ?? null,
    selectCase,
  };
};

/**
 * The whole tab off ONE workflow-manager board request once the lead's case is resolved:
 * GET /v1/cases/:uid/board resolves the participant server-side (loose phone match →
 * PRIMARY) and returns their persona-scoped view with the picked participant on top.
 * useStartWorkflow invalidates the "nawy-now" tree, so a just-started case is re-resolved
 * by externalRef and takes over the board.
 *
 * §NetAudit (2026-08-05): the board used to fetch immediately with phones="" and refetch when
 * the customer's phones landed (the key includes them) — a doubled participants+steps pair on
 * every cold load. The customer runs concurrently with case resolution and is virtually always
 * settled by the time a caseUid exists, so `clientReady` (customer query fetched — success OR
 * error — or no customer to fetch) now gates the board and the first fetch is the right one.
 */
export const useWorkflowBoard = ({
  client,
  clientReady = true,
  journeyUid,
}: {
  client?: CustomerDTO;
  /** customer lookup settled (or none needed) — phones are as refined as they will get */
  clientReady?: boolean;
  journeyUid?: string;
}) => {
  const phoneList = useMemo(
    () => [
      ...new Set(
        (client?.telephones ?? [])
          .map((telephone) => telephone.telephone)
          .filter(Boolean)
      ),
    ],
    [client?.telephones]
  );
  const phones = phoneList.join(",");

  const live = useNawyNowRealtimeLive();
  const { resolutionQuery, cases, selectedCaseUid, selectCase } =
    useCaseSelection(journeyUid);
  const caseUid = selectedCaseUid;

  const boardQuery = useQuery({
    queryKey: ["nawy-now", "board", caseUid, phones],
    keepPreviousData: true,
    staleTime: RESOLUTION_STALE_MS,
    ...realtimeQueryFlags(live),
    enabled: !!caseUid && clientReady,
    queryFn: async (): Promise<ParticipantBoardResponse | null> => {
      try {
        return await getWorkflowBoard(caseUid as string, phones);
      } catch (error) {
        // unknown case → no flow yet: render the start trigger, not the error state
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });

  const view = (caseUid ? boardQuery.data : null) ?? null;
  // §Multi-application: the board in hand belongs to ANOTHER application of this lead — the agent just
  // switched and keepPreviousData is still showing the old one. (A phone refinement of the same case is
  // not a switch.)
  const isSwitching = !!view && view.case.uid !== caseUid;
  const board = useMemo(
    () =>
      view ? deriveBoard(view, view.participant.uid, view.participant.type) : null,
    [view]
  );

  return {
    /** initial load only — refetches (phone refinement, invalidations) keep the board up.
     * §First-paint (live bite, 2026-08-05): the tab used to flash "no application yet" before the
     * board — the journeyUid is undefined while the LEAD PAGE itself loads (resolver returns null
     * instantly), and keepPreviousData masks the real fetch once the uid lands (data stays null ⇒
     * isInitialLoading false). Loading therefore also covers: no journey uid yet, and any fetch
     * that runs while NO case is known — the empty/start state may only render once the resolution
     * has actually settled for the real journey.
     *
     * §Background-refetch (2026-08-08): this used to read `isFetching && data == null`, which is true of
     * EVERY background refetch on a lead that has no case — so each poll / focus refetch replaced the
     * whole tab with a skeleton for the duration of the request, on exactly the leads where the agent is
     * trying to press "Start". `isPreviousData` expresses the real condition (the settled answer in hand
     * belongs to a DIFFERENT journey) without catching refetches of the current one. */
    isLoading:
      !journeyUid ||
      resolutionQuery.isInitialLoading ||
      resolutionQuery.isPreviousData ||
      // a known case whose board hasn't loaded — including while the clientReady gate holds the
      // fetch back (the disabled query reports isInitialLoading false, but we ARE still loading)
      (!!caseUid && (!clientReady || boardQuery.isInitialLoading)),
    isError: resolutionQuery.isError || boardQuery.isError,
    refetch: () => {
      resolutionQuery.refetch();
      if (caseUid) boardQuery.refetch();
    },
    hasCase: !!view,
    board,
    caseUid: view?.case.uid ?? null,
    participantUid: view?.participant.uid ?? null,
    /** §Multi-application: every application on this lead, oldest first. */
    cases,
    /** §Multi-application: the application the page is (or is switching to) — `caseUid` is the board's. */
    selectedCaseUid,
    selectCase,
    isSwitching,
  };
};
